import { FC } from "react";
import {
    AppBar,
    Box,
    IconButton,
    Toolbar,
    Typography,
    useTheme as useMuiTheme,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip
} from "@mui/material";
import tintoaLogoWhite from "../../img/logo_tintoa_white.png";
import tintoaLogoBlack from "../../img/logo_tintoa_black.png";
import {
    Brightness4, 
    Brightness7,
    Dashboard,
    Apps,
    Security,
    People,
    Mail,
    CleaningServices,
    Email,
    Inbox,
    Message
} from "@mui/icons-material";
import { TintoaConfigProvider, TintoaIdentity, TintoaPageBackground, useTintoaConfig } from "@tintoa/ui";
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../theme/ThemeContext';

const drawerWidth = 240;

const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/app/admin' },
    { text: 'Clients', icon: <Apps />, path: '/app/admin/clients' },
    { text: 'Roles', icon: <Security />, path: '/app/admin/roles' },
    { text: 'Users', icon: <People />, path: '/app/admin/users' },
    { text: 'Invitations', icon: <Mail />, path: '/app/admin/invitations' },
    { text: 'Posteingang', icon: <Inbox />, path: '/app/admin/inbox' },
    { text: 'Nachrichten senden', icon: <Message />, path: '/app/admin/messages' },
    { text: 'Data Cleanup', icon: <CleaningServices />, path: '/app/admin/data-cleanup' },
    { text: 'E-Mail Test', icon: <Email />, path: '/app/admin/email-test' },
];

export const AdminLayout: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { toggleColorMode } = useAppTheme();
    const { identityServiceUrl } = useTintoaConfig();
    const muiTheme = useMuiTheme();
    const isDarkTheme = muiTheme.palette.mode === 'dark';

    const handleNavigation = (path: string) => {
        navigate(path);
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Background with blur effect for large screens */}
            <TintoaPageBackground position="fixed" largeScreenOnly />

            {/* Admin Sidebar */}
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        bgcolor: muiTheme.palette.overlay!.subtle,
                        borderRight: 1,
                        borderColor: 'divider',
                        position: 'relative',
                        zIndex: 1,
                    },
                }}
            >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <img
                            height={28}
                            src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                            alt="tintoa-logo"
                            style={{ display: 'block', objectFit: 'contain' }}
                        />
                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                            Admin
                        </Typography>
                    </Box>
                </Box>
                
                <List sx={{ pt: 1 }}>
                    {menuItems.map((item) => (
                        <ListItem key={item.text} disablePadding>
                            <ListItemButton
                                selected={location.pathname === item.path}
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                    mx: 1,
                                    borderRadius: 1
                                }}
                            >
                                <ListItemIcon sx={{ color: 'inherit' }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Drawer>

            {/* Main Content Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Top AppBar */}
                <AppBar 
                    position="static" 
                    color="default" 
                    elevation={0}
                    sx={{
                        bgcolor: muiTheme.palette.overlay!.subtle,
                        borderBottom: 1,
                        borderColor: 'divider',
                        position: 'relative',
                        zIndex: 1,
                    }}
            >
                <Toolbar sx={{ pl: { xs: 2, sm: 3 } }}>
                        <Box sx={{ flexGrow: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title={isDarkTheme ? "Hellmodus" : "Dunkelmodus"}>
                                <IconButton 
                                    onClick={toggleColorMode} 
                                    color="inherit"
                                    sx={{ 
                                        color: 'text.secondary',
                                        '&:hover': { 
                                            bgcolor: muiTheme.palette.overlay!.hover
                                        }
                                    }}
                                >
                                    {isDarkTheme ? <Brightness7 /> : <Brightness4 />}
                                </IconButton>
                            </Tooltip>
                            <TintoaConfigProvider
                                identityServiceUrl={identityServiceUrl}
                                clientId="identity-admin"
                            >
                                <TintoaIdentity
                                    onLogout={() => window.location.replace('/')}
                                />
                            </TintoaConfigProvider>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* Page Content */}
                <Box 
                    component="main" 
                    sx={{ 
                        flexGrow: 1, 
                        p: 3,
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '1400px',
                        }}
                    >
                        <Outlet />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
